// 메신저 프로필 팝업(상대 아바타·이름 클릭)에 «전화번호·직급·직책»(다우)을 덧붙인다.
// 설계: `patch_mm_profile_popover_hr_fields.md`(마스터 2026-10-01 «전화번호, 직급, 직책도 표시할 수 있냐»).
//
// 🔴 MM DOM 은 MM 소유다 — 셀렉터가 어긋나거나 조회가 실패하면 조용히 무동작(팝업이 지금처럼 보인다).
// 🔴 값은 **textContent 로만** 넣는다(innerHTML 금지) — 다우 값이 그대로 화면에 들어오는 자리다.
// 🔴 이메일 줄은 MM 이 `div.user-profile-popover__email[title=<이메일>]` 로 그린다(봇·이메일 비공개·
//    덮어쓰기 프로필이면 그 줄이 아예 없다 → 그때는 아무것도 안 붙인다).
// 🔴 우리가 붙이는 «직급·직책» 줄도 같은 클래스를 쓴다(MM 여백을 그대로 받으려고) → 이메일 줄을
//    고를 때 `.gw-profile-extra` 안은 반드시 뺀다. 안 그러면 제 글을 다시 읽어 조회가 돈다.
// 🔴 클래스만 같아도 모양은 안 따라온다(마스터 2026-10-01 «크기 위치 등이 제각각인데?») — MM 의
//    줄 모양 규칙은 `.user-profile-popover__email` 과 `i.icon-email-outline` 에만 걸려 있어서
//    우리 전화 줄(`__phone`)·우리 아이콘(`icon-phone-outline`·`icon-account-outline`)·직급 줄의
//    `<span>` 은 그 밖이다 → 같은 값을 우리 블록 안으로만 한정해 다시 선언한다(PROFILE_EXTRA_CSS).
export const EMAIL_ROW_CLASS = 'user-profile-popover__email';
export const EMAIL_ROW_SEL = '.' + EMAIL_ROW_CLASS;
export const PHONE_ROW_CLASS = 'user-profile-popover__phone';
export const EXTRA_CLASS = 'gw-profile-extra';
// MM 소유 DOM — 팝업 뿌리와 «이름 아래 직위» 줄(MM 이 이미 그려 주는 값).
export const POPOVER_SEL = '.user-profile-popover';
export const NON_HEADING_SEL = '.user-profile-popover__non-heading';
export const SYNC_THROTTLE_MS = 150;
export const OK_TTL_MS = 600000;    // 성공(없음 포함) 캐시 10분 — 같은 사람 팝업을 여닫아도 1회만 묻는다
export const FAIL_TTL_MS = 60000;   // 실패 캐시 1분 — 끊긴 동안 팝업마다 다시 때리지 않는다

export const PROFILE_EXTRA_STYLE_ID = 'gw-mm-profile-extra-css';

// MM 11.11 이메일 줄 규칙(DEV 번들 `9748.*.css` 실측)을 그대로 옮긴 값 — 폭 238px·여백
// `0 16px 10px 16px`·아이콘 간격 8px·글꼴 14px/20px. 이메일 줄과 같은 자리·같은 크기로 보이게 한다.
// 🔴 선택자를 `.gw-profile-extra` 안으로만 한정한다 — MM 의 다른 줄(이메일·현지시간)에 번지면 안 된다.
// 🔴 아이콘 ::before 의 여백을 0 으로 되돌리는 줄이 핵심이다 — MM 공통 규칙
//    `[class*=" icon-"]:before{margin-right:.2em}`(3949.*.css)이 우리 아이콘에도 걸려 있어서,
//    이걸 안 지우면 아이콘과 글자 사이가 이메일 줄보다 .2em 넓어진다(이메일 아이콘만 면제돼 있다).
export const PROFILE_EXTRA_CSS = [
  '.' + EXTRA_CLASS + ' > div{display:flex;width:238px;align-items:center;',
  'padding:0 16px 10px 16px;justify-self:start}',
  '.' + EXTRA_CLASS + ' > div > i.icon{margin-inline-end:8px}',
  '.' + EXTRA_CLASS + ' > div > i.icon::before{margin-inline-start:0;margin-inline-end:0}',
  '.' + EXTRA_CLASS + ' > div > a,.' + EXTRA_CLASS + ' > div > span{',
  'font-family:"Open Sans";font-size:14px;font-weight:400;line-height:20px;',
  'overflow:hidden;text-overflow:ellipsis;white-space:nowrap}',
].join('');

// style#gw-mm-profile-extra-css 를 head 에 '1개만' 유지한다(멱등 — `mmStickerSizeCss` 와 같은 형태).
// head 가 없으면(로드 중 문서) 조용히 no-op.
function applyProfileExtraCss(doc) {
  if (!doc || !doc.head) return;
  const found = doc.getElementById(PROFILE_EXTRA_STYLE_ID);
  if (found) {
    if (found.textContent !== PROFILE_EXTRA_CSS) found.textContent = PROFILE_EXTRA_CSS;
    return;
  }
  const st = doc.createElement('style');
  st.id = PROFILE_EXTRA_STYLE_ID;
  st.textContent = PROFILE_EXTRA_CSS;
  doc.head.appendChild(st);
}

function removeProfileExtraCss(doc) {
  try {
    const st = doc && doc.getElementById ? doc.getElementById(PROFILE_EXTRA_STYLE_ID) : null;
    if (st && st.parentNode) st.parentNode.removeChild(st);
  } catch (e) { /* */ }
}

function clean(v) {
  if (v === null || v === undefined) return '';
  try { return String(v).trim(); } catch (e) { return ''; }
}

// {phone, position, duty} → [{kind, text}]. 빈 값은 줄을 만들지 않는다.
// 🔴 직위와 직책이 같거나(센터장/센터장) 한쪽이 다른 쪽을 품으면(총괄센터장/센터장) **한 값만** 낸다 —
//    다우 실데이터에 그런 사람이 있어서, 그대로 이으면 «센터장 · 센터장» 이 보인다.
export function formatLines(info) {
  const o = info || {};
  const phone = clean(o.phone);
  const position = clean(o.position);
  const duty = clean(o.duty);
  const out = [];
  if (phone) out.push({ kind: 'phone', text: phone });
  let title = '';
  if (position && duty) {
    if (position === duty) title = position;
    else if (position.indexOf(duty) >= 0) title = position;
    else if (duty.indexOf(position) >= 0) title = duty;
    else title = position + ' · ' + duty;
  } else {
    title = position || duty;
  }
  if (title) out.push({ kind: 'title', text: title });
  return out;
}

// formatLines 결과에서 «이름 아래 직위와 똑같은» 직급 줄을 뺀다(반환 = 새 배열 · 입력 불변).
// 왜: MM 팝업은 이름 바로 아래에 제 프로필 직위(`__non-heading`)를 이미 보여 준다 — 다우 직위도
//     «파트장» 이면 같은 값이 두 줄로 보인다(마스터 2026-10-01 캡처). 전화 줄은 MM 에 없으니 남긴다.
// 🔴 '정확히 같을 때만' 뺀다 — «파트장» vs «파트장 · 팀장» 처럼 더 알려 주는 값은 그대로 그린다.
export function dropDuplicateTitle(lines, texts) {
  const rows = Array.isArray(lines) ? lines : [];
  const seen = [];
  const src = Array.isArray(texts) ? texts : [];
  for (let i = 0; i < src.length; i += 1) {
    const t = clean(src[i]);
    if (t) seen.push(t);
  }
  if (!seen.length) return rows.slice();
  return rows.filter((line) => !(line && line.kind === 'title' && seen.indexOf(clean(line.text)) >= 0));
}

// 이 이메일 줄이 든 팝업에서 MM 이 그린 직위 글자들 — 잘린 표시(textContent)와 전체 값(title) 둘 다.
// 🔴 읽기만 한다(MM DOM 수정 금지). 팝업 뿌리를 못 찾으면 빈 배열 = 걸러내기 없음.
function popoverTitleTexts(row) {
  const out = [];
  try {
    const pop = row && typeof row.closest === 'function' ? row.closest(POPOVER_SEL) : null;
    if (!pop || typeof pop.querySelectorAll !== 'function') return out;
    const list = pop.querySelectorAll(NON_HEADING_SEL);
    for (let i = 0; i < list.length; i += 1) {
      const el = list[i];
      out.push(clean(el.textContent));
      if (el.getAttribute) out.push(clean(el.getAttribute('title')));
    }
  } catch (e) { /* MM DOM 변동 — 걸러내기만 건너뛴다 */ }
  return out;
}

// 'tel:' 링크에 쓸 값 — 숫자와 + 만 남긴다(빈 값이면 링크를 걸지 않는다).
export function telHref(phone) {
  const digits = clean(phone).replace(/[^0-9+]/g, '');
  return digits ? 'tel:' + digits : '';
}

// 이메일 줄이 가리키는 사람 — title 속성이 정본, 없으면 mailto 링크 텍스트.
export function rowEmail(row) {
  if (!row) return '';
  let raw = '';
  try {
    raw = row.getAttribute ? (row.getAttribute('title') || '') : '';
    if (!raw && typeof row.querySelector === 'function') {
      const link = row.querySelector('a[href^="mailto:"]');
      raw = (link && link.textContent) || '';
    }
  } catch (e) { return ''; }
  return clean(raw).toLowerCase();
}

// doc 당 1회 설치(모양 CSS 주입 포함). 반환 = dispose(옵저버·타이머 해제 + 붙인 블록·style 제거).
// 이미 설치돼 있으면 null — 폴링+onLoad 이중 호출이라 style 도 블록도 한 벌만 남는다.
// 🔴 `attributes` + attributeFilter ['title'] 가 핵심이다 — 팝업을 닫지 않고 다른 사람을 눌러도
//    MM 은 같은 노드의 title/링크만 갈아 끼울 수 있다(childList 만 보면 옛 사람 정보가 남는다).
export function setupMmProfileExtra(doc, options) {
  const opts = options || {};
  if (!doc || !doc.body || doc.__gwMmProfileExtra) return null;
  const isActive = typeof opts.isActive === 'function' ? opts.isActive : () => true;
  const fetchExtra = typeof opts.fetchExtra === 'function' ? opts.fetchExtra : null;
  const okTtlMs = Number.isFinite(opts.okTtlMs) ? opts.okTtlMs : OK_TTL_MS;
  const failTtlMs = Number.isFinite(opts.failTtlMs) ? opts.failTtlMs : FAIL_TTL_MS;
  doc.__gwMmProfileExtra = true;
  try { applyProfileExtraCss(doc); } catch (e) { /* head 없음 — 모양만 MM 기본값이 된다 */ }
  let disposed = false;
  let timer = null;
  const cache = new Map();      // email → {at, failed, data} — 이 문서가 사는 동안만
  const inflight = new Set();

  function removeBlocks() {
    try {
      const list = doc.querySelectorAll('.' + EXTRA_CLASS);
      for (let i = 0; i < list.length; i += 1) {
        const el = list[i];
        if (el.parentNode) el.parentNode.removeChild(el);
      }
    } catch (e) { /* */ }
  }

  // row 바로 뒤에 블록을 붙인다. 그릴 줄이 없으면(다우에 값이 없다 · 직급이 이름 아래 직위와
  // 똑같아 걸러졌다) 아무것도 안 붙인다.
  function draw(row, email, data) {
    const lines = dropDuplicateTitle(
      formatLines(data && data.found ? data : null), popoverTitleTexts(row),
    );
    if (!lines.length) return null;
    try {
      const block = doc.createElement('div');
      block.className = EXTRA_CLASS;
      block.setAttribute('data-email', email);   // 누구 것인지 — 사람이 바뀌면 이 값으로 걷어낸다
      for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        const isPhone = line.kind === 'phone';
        const wrap = doc.createElement('div');
        wrap.className = isPhone ? PHONE_ROW_CLASS : EMAIL_ROW_CLASS;
        const icon = doc.createElement('i');
        icon.className = 'icon ' + (isPhone ? 'icon-phone-outline' : 'icon-account-outline');
        icon.setAttribute('aria-hidden', 'true');
        wrap.appendChild(icon);
        const href = isPhone ? telHref(line.text) : '';
        const value = doc.createElement(href ? 'a' : 'span');
        if (href) value.setAttribute('href', href);
        value.textContent = line.text;           // 🔴 textContent 만 — 다우 값은 평문이다
        wrap.appendChild(value);
        block.appendChild(wrap);
      }
      if (!row.parentNode) return null;
      row.parentNode.insertBefore(block, row.nextSibling);
      return block;
    } catch (e) { return null; }
  }

  function sync() {
    if (disposed || !isActive()) return;
    let rows = null;
    try {
      // ① 고아 정리 먼저 — 앞 형제가 «그 이메일의» 이메일 줄이 아닌 블록은 지운다
      //    (팝업이 닫혔다 · 같은 노드에서 사람이 바뀌었다 · MM 이 줄을 통째로 다시 그렸다).
      const blocks = doc.querySelectorAll('.' + EXTRA_CLASS);
      for (let i = 0; i < blocks.length; i += 1) {
        const el = blocks[i];
        const prev = el.previousElementSibling;
        const keep = !!prev && !!prev.classList && prev.classList.contains(EMAIL_ROW_CLASS)
          && rowEmail(prev) === (el.getAttribute('data-email') || '');
        if (!keep && el.parentNode) el.parentNode.removeChild(el);
      }
      rows = doc.querySelectorAll(EMAIL_ROW_SEL);
    } catch (e) { return; }

    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      let mine = true;
      try { mine = !!(row.closest && row.closest('.' + EXTRA_CLASS)); } catch (e) { mine = true; }
      if (mine) continue;                       // 우리가 붙인 직급 줄(같은 클래스)은 이메일 줄이 아니다
      const email = rowEmail(row);
      if (!email) continue;
      const next = row.nextElementSibling;
      if (next && next.classList && next.classList.contains(EXTRA_CLASS)
          && next.getAttribute('data-email') === email) continue;    // 이미 그려져 있다

      const hit = cache.get(email);
      const now = Date.now();
      if (hit && (now - hit.at) < (hit.failed ? failTtlMs : okTtlMs)) {
        if (!hit.failed) draw(row, email, hit.data);
        continue;
      }
      if (inflight.has(email) || !fetchExtra) continue;
      inflight.add(email);
      let p = null;
      try { p = fetchExtra(email); } catch (e) { p = null; }
      if (!p || typeof p.then !== 'function') {
        inflight.delete(email);
        cache.set(email, { at: Date.now(), failed: true, data: null });
        continue;
      }
      // 🔴 응답이 오면 «잡아 둔 row» 가 아니라 sync 를 다시 예약한다 — 기다리는 사이 팝업을 닫았다
      //    다시 열면 그 row 는 이미 문서에서 떨어져 나가 거기 그려 봐야 아무 데도 안 보인다.
      p.then(
        (data) => { cache.set(email, { at: Date.now(), failed: false, data: data || null }); },
        () => { cache.set(email, { at: Date.now(), failed: true, data: null }); },
      ).then(() => {
        inflight.delete(email);
        kick();
      });
    }
  }

  function kick() {
    if (disposed || timer) return;    // 쓰로틀 — 메신저 본문은 프레임마다 변이가 난다
    timer = setTimeout(() => {
      timer = null;
      try { sync(); } catch (e) { /* MM DOM 변동 — 무동작 */ }
    }, SYNC_THROTTLE_MS);
  }

  try { sync(); } catch (e) { /* */ }   // 설치 즉시 1회(이미 열려 있는 팝업도 잡는다)

  let obs = null;
  try {
    const MO = (doc.defaultView && doc.defaultView.MutationObserver)
      || (typeof window !== 'undefined' ? window.MutationObserver : null);
    if (MO) {
      // 🔴 팝업이 아니라 doc.body 에 건다 — 팝오버는 열 때마다 새로 만들어진다.
      obs = new MO(kick);
      obs.observe(doc.body, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['title'],
      });
    }
  } catch (e) { obs = null; }

  return function dispose() {
    if (disposed) return;
    disposed = true;                  // 남은 콜백·타이머·비행 중 응답은 여기서부터 무동작
    try { if (obs) obs.disconnect(); } catch (e) { /* */ }
    if (timer) { clearTimeout(timer); timer = null; }
    removeBlocks();
    removeProfileExtraCss(doc);        // 주입한 style 도 거둬 간다(설치 흔적 0)
    try { doc.__gwMmProfileExtra = false; } catch (e) { /* */ }
  };
}
